import React, { useState, useEffect } from 'react';
import { History, Search, Download, CheckCircle, Edit, Trash2, X, Save, AlertTriangle } from 'lucide-react';
import * as XLSX from 'xlsx';
import axios from 'axios';

const HistoryPage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // ✅ Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [updating, setUpdating] = useState(false);

  // ✅ Edit Form State
  const [editForm, setEditForm] = useState({
      id: null,
      rx_no: '',
      drug_name: '',
      quantity: '',
      days_supply: '',
      expiration_date: '',
      status: ''
  });

  // Change this to match your local folder path
const API_URL = 'https://unifyrxcrm.com/AG-BACKEND/index.php';
  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}?action=get_history`);
      if (Array.isArray(response.data)) setRecords(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching history:", error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return '-';
    // Fix timezone offset issue by treating as string
    const datePart = dateString.split(' ')[0];
    const [year, month, day] = datePart.split('-');
    if (year && month && day) return `${month}/${day}/${year}`;
    return dateString;
  };

  const filteredRecords = records.filter(item => 
    (item.full_name && item.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.drug_name && item.drug_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.rx_no && item.rx_no.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleDownloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(records);
    XLSX.utils.book_append_sheet(wb, ws, "Delivered_History");
    XLSX.writeFile(wb, "Delivered_Prescriptions.xlsx");
  };

  // ✅ HANDLERS
  const handleEditClick = (item) => {
      // Fix date format for input field (YYYY-MM-DD)
      const cleanDate = (item.expiration_date && item.expiration_date !== '0000-00-00') 
          ? item.expiration_date.split(' ')[0] 
          : '';

      setEditForm({
          id: item.id,
          rx_no: item.rx_no,
          drug_name: item.drug_name,
          quantity: item.quantity,
          days_supply: item.days_supply || 0,
          expiration_date: cleanDate,
          status: item.status
      });
      setShowEditModal(true);
  };

  const handleDeleteClick = (item) => {
      setItemToDelete(item);
      setShowDeleteModal(true);
  };

  const executeDelete = async () => {
      if (!itemToDelete) return;
      try {
          await axios.post(`${API_URL}?action=delete_prescription`, { id: itemToDelete.id });
          setShowDeleteModal(false);
          fetchHistory(); // Refresh list
      } catch (error) {
          console.error(error);
          alert("Failed to delete record.");
      }
  };

  const handleUpdateSubmit = async (e) => {
      e.preventDefault();
      setUpdating(true);
      try {
          // Using update_prescription_modal action which handles quick edits
          await axios.post(`${API_URL}?action=update_prescription_modal`, editForm);
          alert("Record updated successfully!");
          setShowEditModal(false);
          fetchHistory(); // Refresh list
      } catch (error) {
          console.error(error);
          alert("Error updating record.");
      } finally {
          setUpdating(false);
      }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-700 flex items-center gap-2">
          <History className="text-blue-600" /> Delivered Prescription History
        </h2>

        <div className="flex gap-3">
            <div className="relative">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input 
                    type="text" 
                    placeholder="Search records..." 
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" 
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            <button onClick={handleDownloadExcel} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2 shadow">
                <Download size={16} /> Excel
            </button>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 border-b border-black">
            <tr>
              <th className="px-6 py-3 text-left font-bold text-gray-900 uppercase">Patient</th>
              <th className="px-6 py-3 text-left font-bold text-gray-900 uppercase">RX No.</th>
              <th className="px-6 py-3 text-left font-bold text-gray-900 uppercase">Drug Name</th>
              <th className="px-6 py-3 text-center font-bold text-gray-900 uppercase">Qty</th>
              <th className="px-6 py-3 text-left font-bold text-gray-900 uppercase">Expiration Date</th>
              <th className="px-6 py-3 text-center font-bold text-gray-900 uppercase">Status</th>
              {/* ✅ NEW COLUMN */}
              <th className="px-6 py-3 text-center font-bold text-gray-900 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
                <tr><td colSpan="7" className="p-4 text-center">Loading history...</td></tr>
            ) : filteredRecords.length === 0 ? (
                <tr><td colSpan="7" className="p-6 text-center text-gray-500">No delivered records found.</td></tr>
            ) : (
                filteredRecords.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 font-mono border-b">
                    <td className="px-6 py-3 text-blue-600 font-bold">{item.full_name}</td>
                    <td className="px-6 py-3 text-gray-900">{item.rx_no}</td>
                    <td className="px-6 py-3 text-gray-900 font-bold">{item.drug_name}</td>
                    <td className="px-6 py-3 text-center">{item.quantity}</td>
                    <td className="px-6 py-3 text-gray-600">{formatDate(item.expiration_date)}</td>
                    
                    <td className="px-6 py-3 text-center">
                        <span className="bg-green-100 text-green-800 text-[10px] font-bold px-2 py-1 rounded flex items-center justify-center gap-1">
                            <CheckCircle size={12}/> {item.status}
                        </span>
                    </td>

                    {/* ✅ EDIT & DELETE BUTTONS */}
                    <td className="px-6 py-3 text-center flex justify-center gap-2">
                        <button onClick={() => handleEditClick(item)} className="text-blue-500 hover:bg-blue-100 p-1.5 rounded" title="Edit">
                            <Edit size={16}/>
                        </button>
                        <button onClick={() => handleDeleteClick(item)} className="text-red-500 hover:bg-red-100 p-1.5 rounded" title="Delete">
                            <Trash2 size={16}/>
                        </button>
                    </td>
                </tr>
                ))
            )}
          </tbody>
        </table>
      </div>

      {/* ✅ EDIT MODAL */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-96 p-6 relative">
                <button onClick={() => setShowEditModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>
                <h3 className="text-lg font-bold mb-4 text-blue-700">Edit History Record</h3>
                
                <form onSubmit={handleUpdateSubmit} className="space-y-3">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Drug Name</label>
                        <input type="text" className="w-full border p-2 rounded text-sm" value={editForm.drug_name} onChange={e => setEditForm({...editForm, drug_name: e.target.value})}/>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Quantity</label>
                            <input type="number" className="w-full border p-2 rounded text-sm" value={editForm.quantity} onChange={e => setEditForm({...editForm, quantity: e.target.value})}/>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Days Supply</label>
                            <input type="number" className="w-full border p-2 rounded text-sm" value={editForm.days_supply} onChange={e => setEditForm({...editForm, days_supply: e.target.value})}/>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Expiration Date</label>
                        <input type="date" className="w-full border p-2 rounded text-sm" value={editForm.expiration_date} onChange={e => setEditForm({...editForm, expiration_date: e.target.value})}/>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Status</label>
                        <select className="w-full border p-2 rounded text-sm" value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})}>
                            <option value="Delivered">Delivered</option>
                            <option value="Picked">Picked</option>
                            <option value="Processing">Processing</option>
                            <option value="Pending">Pending</option>
                        </select>
                    </div>

                    <button type="submit" disabled={updating} className="w-full bg-blue-600 text-white font-bold py-2 rounded hover:bg-blue-700 flex items-center justify-center gap-2 mt-2">
                        {updating ? 'Saving...' : <><Save size={16} /> Save Changes</>}
                    </button>
                </form>
            </div>
        </div>
      )}

      {/* ✅ DELETE MODAL */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-96 p-6 relative text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Confirm Deletion</h3>
                <p className="text-sm text-gray-500 mt-2">
                    Are you sure you want to remove this record for <strong>{itemToDelete.rx_no}</strong>?
                </p>
                <div className="mt-6 flex justify-center gap-3">
                    <button onClick={() => setShowDeleteModal(false)} className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded font-medium hover:bg-gray-50">Cancel</button>
                    <button onClick={executeDelete} className="bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700">Delete</button>
                </div>
            </div>
        </div>
      )}

    </div>
  );
};

export default HistoryPage;